package fr.frux.core.common.directions

import fr.frux.core.common.format.formatFixed

/**
 * Ouverture d'un itinéraire vers un point (#335), sur le modèle exact de [fr.frux.core.common.io.FileExporter] :
 * chaque plateforme a sa propre notion de « lancer une navigation » (chooser système Android,
 * URL Apple Maps sur iOS, nouvel onglet sur le web), aucune API commune ne les couvre.
 *
 * Pas de suivi du trajet en retour : ce que l'appelant obtient, c'est un geste proposé à
 * l'utilisateur, jamais une confirmation d'arrivée — même contrat que `FileExporter.export`.
 */
interface DirectionsLauncher {
    /**
     * Propose un itinéraire vers ([latitude], [longitude]), étiqueté [label] quand la plateforme
     * le permet (nom de l'espèce, tel qu'affiché à l'écran).
     *
     * @return `true` si le geste a été proposé à l'utilisateur (chooser ouvert, onglet ouvert),
     *   `false` si l'opération a échoué.
     */
    fun openDirections(
        latitude: Double,
        longitude: Double,
        label: String,
    ): Boolean
}

expect fun createDirectionsLauncher(): DirectionsLauncher

/**
 * Formate une coordonnée en décimal fixe à 6 chiffres après la virgule (précision largement
 * suffisante pour un itinéraire) via [formatFixed] plutôt que `Double.toString()` : ce dernier ne
 * rend pas le même texte sur toutes les cibles KMP (ex. `45.0` devient `"45.0"` sur JVM/Native
 * mais `"45"` sur JS), ce qui casserait une URL construite en commonMain différemment selon la
 * plateforme appelante.
 */
internal fun formatCoordinate(value: Double): String = value.formatFixed(COORDINATE_DECIMALS)

private const val COORDINATE_DECIMALS = 6

/**
 * Encodeur pourcent minimal (RFC 3986, jeu "unreserved") : `java.net.URLEncoder` n'existe pas en
 * commonMain. Le label vient de contenu utilisateur/éditorial (espaces, apostrophes, accents) et
 * doit rester lisible dans une URL de navigation sans la casser.
 */
internal fun percentEncode(value: String): String {
    val builder = StringBuilder()
    for (byte in value.encodeToByteArray()) {
        val unsignedByte = byte.toInt() and 0xFF
        val char = unsignedByte.toChar()
        if (char in UNRESERVED_CHARS) {
            builder.append(char)
        } else {
            builder.append('%')
            builder.append(unsignedByte.toString(radix = 16).uppercase().padStart(2, '0'))
        }
    }
    return builder.toString()
}

private val UNRESERVED_CHARS = (('A'..'Z') + ('a'..'z') + ('0'..'9') + listOf('-', '_', '.', '~')).toSet()

/** URI `geo:` Android — laisse le chooser système proposer Google Maps, Waze, OsmAnd… */
internal fun buildGeoUri(
    latitude: Double,
    longitude: Double,
    label: String,
): String {
    val lat = formatCoordinate(latitude)
    val lon = formatCoordinate(longitude)
    val encodedLabel = percentEncode(label)
    return "geo:$lat,$lon?q=$lat,$lon($encodedLabel)"
}

/** URL Google Maps, repli universel (Android sans app dédiée, web, desktop). */
internal fun buildGoogleMapsUrl(
    latitude: Double,
    longitude: Double,
): String {
    val lat = formatCoordinate(latitude)
    val lon = formatCoordinate(longitude)
    return "https://www.google.com/maps/dir/?api=1&destination=$lat,$lon"
}

/** URL Apple Maps — seule cible ouvrant nativement l'app Plans sur iOS. */
internal fun buildAppleMapsUrl(
    latitude: Double,
    longitude: Double,
): String {
    val lat = formatCoordinate(latitude)
    val lon = formatCoordinate(longitude)
    return "https://maps.apple.com/?daddr=$lat,$lon"
}
